import React from 'react';
import { QueryClient } from '@tanstack/react-query';

import { RoverLoading } from '@rover/react-lib/src/components';
import getQueryError from '@rover/react-lib/src/utils/getQueryError';
import {
  apiCitiesAndNeighborhoodsList,
  getApiCitiesAndNeighborhoodsListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-cities-and-neighborhoods-list';
import {
  apiCountryFooterLinksList,
  getApiCountryFooterLinksListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-country-footer-links-list';
import {
  apiCountryRegionLinksList,
  getApiCountryRegionLinksListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-country-region-links-list';
import {
  apiFrontendConfigurationRetrieve,
  getApiFrontendConfigurationRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-configuration-retrieve';
import {
  apiFrontendServiceTypesList,
  getApiFrontendServiceTypesListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-service-types-list';
import { EngineEnum } from '@rover/rsdk/src/apiClient/latest/model/engineEnum';
import type { FrontendConfiguration } from '@rover/rsdk/src/apiClient/latest/model/frontendConfiguration';
import { HttpError } from '@rover/rsdk/src/errors';
import {
  FLAGS,
  isFlagActiveForConfig,
  useFrontendConfiguration,
} from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import { captureMessage } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import useFrontendConfigurationShim from '@rover/shared/js/hooks/useFrontendConfigurationShim';
import useServiceLanding, {
  type ServiceLandingPathDescriptor,
  getServiceLandingFetchArgs,
} from '@rover/shared/js/hooks/useServiceLanding';
import { getPath, getURL } from '@rover/shared/js/utils/getFullUrl';
import getRoverBrand from '@rover/utilities/getRoverBrand';
import { Http404Error, Http500Error } from '@rover/utilities/HttpError';
import { getCountryUrlPrefix, US_COUNTRY_URL_PREFIX } from '@rover/utilities/roverGeo.constants';
import { redirect } from '@rover/utilities/url';

import GroomingServiceLandingContainer from './containers/GroomingServiceLandingContainer/GroomingServiceLandingContainer';
import LegacyServiceLandingContainer from './containers/LegacyServiceLandingContainer/LegacyServiceLandingContainer';
import ServiceLandingContainer from './containers/ServiceLandingContainer/ServiceLandingContainer';
import parseServiceLandingPath from './parsers';
import { ServiceLandingEngineContainer } from './types';

const ENGINE_CONTAINERS: Record<EngineEnum, ServiceLandingEngineContainer> = {
  [EngineEnum.template]: ServiceLandingContainer,
  [EngineEnum.legacy]: LegacyServiceLandingContainer,
  [EngineEnum.grooming]: GroomingServiceLandingContainer,
  [EngineEnum.training]: LegacyServiceLandingContainer,
};

const getEngineContainer = (engine: EngineEnum): ServiceLandingEngineContainer => {
  const container = ENGINE_CONTAINERS[engine];
  if (container) return container;

  captureMessage('Unmapped service landing engine', {
    tags: { pathname: getPath(), engine },
  });
  return LegacyServiceLandingContainer;
};

// Render reads the configuration off the `useFrontendConfiguration` hook. The
// prefetch has no hook, so it reads the same payload through the query client,
// which the server render already warmed with the frontend configuration.
const getFrontendConfiguration = (queryClient: QueryClient): Promise<FrontendConfiguration> =>
  queryClient.ensureQueryData({
    queryKey: getApiFrontendConfigurationRetrieveQueryKey(),
    queryFn: () => apiFrontendConfigurationRetrieve(),
  });

const getIsGroomingServicePageEnabled = async (queryClient: QueryClient): Promise<boolean> => {
  const configuration = await getFrontendConfiguration(queryClient);
  return isFlagActiveForConfig(
    configuration?.flags ?? {},
    FLAGS.ROLLOUT_GROOMING_SEO_SERVICE_PAGES,
    false
  );
};

const BaseServiceLandingContainer: React.FC & { reactQueryPrefetch: any } = () => {
  const path = getPath();
  // always redirect `/us/*`
  if (getCountryUrlPrefix(path) === US_COUNTRY_URL_PREFIX) {
    // same exact location, just trim off the leading bit
    const location = getURL();
    location.pathname = location.pathname.slice('/us'.length);
    redirect(location);
  }

  const { language, settings } = useFrontendConfigurationShim();
  const descriptor = parseServiceLandingPath(path, getRoverBrand(), {
    hostname: getURL().hostname,
    language,
    countryCode: settings?.countryCode,
  });

  const { isFlagActive } = useFrontendConfiguration();
  const isGroomingServicePageEnabled = isFlagActive(FLAGS.ROLLOUT_GROOMING_SEO_SERVICE_PAGES);

  const {
    data: serviceLandingData,
    isLoading,
    isError,
    error: serviceLandingError,
  } = useServiceLanding(descriptor);

  if (serviceLandingError instanceof HttpError && serviceLandingError.statusCode === 404) {
    throw new Http404Error();
  }

  if (isError) throw new Http500Error();

  if (isLoading) {
    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  if (!serviceLandingData) return null;

  const engine = serviceLandingData.configuration?.engine ?? EngineEnum.legacy;

  if (engine === EngineEnum.grooming && !isGroomingServicePageEnabled) {
    throw new Http404Error();
  }

  const EngineContainer = getEngineContainer(engine);

  return <EngineContainer serviceLandingData={serviceLandingData} descriptor={descriptor} />;
};

/**
 * The queries every engine needs, whichever template it renders. Runtime twin of
 * `useServiceLandingPageData`: a query added to one belongs in the other.
 */
const prefetchSharedQueries = (
  queryClient: QueryClient,
  descriptor: ServiceLandingPathDescriptor,
  serviceType: string | undefined
): Promise<unknown> => {
  const { countryUrlPrefix } = descriptor;

  return Promise.all([
    queryClient.prefetchQuery({
      queryKey: getApiCitiesAndNeighborhoodsListQueryKey({ countryUrlPrefix, serviceType }),
      queryFn: () => apiCitiesAndNeighborhoodsList({ countryUrlPrefix, serviceType }),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiCountryFooterLinksListQueryKey({ countryUrlPrefix }),
      queryFn: () => apiCountryFooterLinksList({ countryUrlPrefix }),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiCountryRegionLinksListQueryKey({ countryUrlPrefix }),
      queryFn: () => apiCountryRegionLinksList({ countryUrlPrefix }),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiFrontendServiceTypesListQueryKey(),
      queryFn: () => apiFrontendServiceTypesList(),
    }),
  ]);
};

BaseServiceLandingContainer.reactQueryPrefetch = async (queryClient: QueryClient) => {
  const path = getPath();
  // The descriptor has to come from the same source on both sides of hydration:
  // the server resolves the language and country from request headers, which the
  // client cannot see, so both read the configuration the backend derived from
  // them. Deriving them separately gives the two renders different query keys.
  const { settings } = await getFrontendConfiguration(queryClient);
  const descriptor = parseServiceLandingPath(path, getRoverBrand(), {
    hostname: getURL().hostname,
    language: settings?.language,
    countryCode: settings?.countryCode,
  });

  const serviceLandingData = await queryClient
    .fetchQuery(getServiceLandingFetchArgs(descriptor))
    .catch((error) => {
      throw getQueryError(error) ?? error;
    });

  const engine = serviceLandingData?.configuration?.engine ?? EngineEnum.legacy;

  if (engine === EngineEnum.grooming && !(await getIsGroomingServicePageEnabled(queryClient))) {
    throw new Http404Error();
  }

  const EngineContainer = getEngineContainer(engine);

  return Promise.all([
    prefetchSharedQueries(queryClient, descriptor, serviceLandingData?.serviceLandingUrlSlug),
    EngineContainer.reactQueryPrefetch(queryClient, { serviceLandingData, descriptor }),
  ]);
};

export default BaseServiceLandingContainer;
